import type { InstanceChangeDays } from "@keydials/api-client";
import { toInputDate } from "./compareModel";

/**
 * The timeline scrubber's arithmetic (KEY-LOOKBACK PR 4): days along a track from the dial's creation to
 * today, the ticks, snapping and keyboard stepping. Pure, so the rules a reader feels under the pointer and
 * the arrow keys are tested without a layout engine — jsdom has no widths.
 *
 * Days are `yyyy-mm-dd` calendar days throughout, the date field's own form. The server groups changes by
 * UTC day; the few that land either side of midnight in summer sit one tick early, which the picker forgives
 * because it reads a chosen day as its end.
 */

/** Whole days between two `yyyy-mm-dd` days, `b − a`. Counted on UTC noon so a clock change can't skew it. */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86_400_000);
}

/** `day` moved by `n` days. */
export function addDays(day: string, n: number): string {
  const d = new Date(Date.parse(`${day}T12:00:00Z`) + n * 86_400_000);
  return d.toISOString().slice(0, 10);
}

export interface Timeline {
  /** The first day on the track: the dial's creation. */
  start: string;
  /** The last: today. */
  end: string;
  /** Days along the track, `end − start`, at least 1 so a dial made today still has a track. */
  span: number;
  /** Change days inside the track, oldest first, each with its count. */
  ticks: { day: string; count: number }[];
  /** The busiest day's count, for scaling tick heights. */
  maxCount: number;
}

export function buildTimeline(changeDays: InstanceChangeDays, today: Date = new Date()): Timeline {
  const end = toInputDate(today);
  const created = new Date(changeDays.createdAtUtc);
  let start = Number.isNaN(created.getTime()) ? end : toInputDate(created);
  if (daysBetween(start, end) < 0) start = end;
  const ticks = changeDays.days.filter((d) => daysBetween(start, d.day) >= 0 && daysBetween(d.day, end) >= 0);
  return {
    start,
    end,
    span: Math.max(1, daysBetween(start, end)),
    ticks,
    maxCount: ticks.reduce((m, t) => Math.max(m, t.count), 0),
  };
}

/** A day clamped onto the track. */
export function clampDay(t: Timeline, day: string): string {
  if (daysBetween(t.start, day) < 0) return t.start;
  if (daysBetween(day, t.end) < 0) return t.end;
  return day;
}

/** Where a day sits along the track, 0…1. */
export function fractionOf(t: Timeline, day: string): number {
  return Math.min(1, Math.max(0, daysBetween(t.start, day) / t.span));
}

/** The day under a point `fraction` (0…1) of the way along. */
export function dayAtFraction(t: Timeline, fraction: number): string {
  return addDays(t.start, Math.round(Math.min(1, Math.max(0, fraction)) * t.span));
}

/** How far a tick can pull the handle, in pixels. Close enough to a change day, and that's the day you meant. */
export const SNAP_PX = 4;

/**
 * The day under the pointer, snapped to the nearest change day when one is within {@link SNAP_PX}.
 * `x` and `width` are the pointer's offset along the track and the track's width, both in pixels.
 */
export function dayAtPointer(t: Timeline, x: number, width: number): string {
  if (width <= 0) return t.end;
  const raw = dayAtFraction(t, x / width);
  let best: { day: string; dist: number } | null = null;
  for (const tick of t.ticks) {
    const dist = Math.abs(fractionOf(t, tick.day) * width - x);
    if (dist <= SNAP_PX && (!best || dist < best.dist)) best = { day: tick.day, dist };
  }
  return best?.day ?? raw;
}

/** A tick's height in the 36px track: 6px for the quietest day, 12px for the busiest. */
export function tickHeight(t: Timeline, count: number): number {
  if (t.maxCount <= 1) return 6;
  return 6 + ((count - 1) / (t.maxCount - 1)) * 6;
}

/** The change day before (dir −1) or after (dir 1) `day`, or `day` itself when there is none that way. */
export function stepChangeDay(t: Timeline, day: string, dir: -1 | 1): string {
  if (dir > 0) return t.ticks.find((tick) => daysBetween(day, tick.day) > 0)?.day ?? day;
  for (let i = t.ticks.length - 1; i >= 0; i--) {
    if (daysBetween(t.ticks[i].day, day) > 0) return t.ticks[i].day;
  }
  return day;
}

/** A week back or forward, kept on the track. */
export function stepWeek(t: Timeline, day: string, dir: -1 | 1): string {
  return clampDay(t, addDays(day, 7 * dir));
}

/** How many changes landed on `day` (0 when none). */
export function countOn(t: Timeline, day: string): number {
  return t.ticks.find((tick) => tick.day === day)?.count ?? 0;
}

/** "Sat 14 Mar 2026" — the drag tooltip and the slider's spoken value. */
export function fmtScrubDay(day: string): string {
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  // Built from parts: en-GB puts a comma after the weekday ("Sat, 14 Mar"), which the design doesn't.
  const weekday = d.toLocaleDateString("en-GB", { weekday: "short" });
  return `${weekday} ${d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
}

/** "3 changes that day" / "1 change that day" / "No changes that day". */
export function changesThatDay(count: number): string {
  if (count === 0) return "No changes that day";
  return `${count} ${count === 1 ? "change" : "changes"} that day`;
}

/** "Created · Jan 2025", the track's left label. */
export function createdLabel(t: Timeline): string {
  const d = new Date(`${t.start}T12:00:00`);
  return `Created · ${d.toLocaleDateString("en-GB", { month: "short", year: "numeric" })}`;
}
